// ============================================
// SUBDOMAIN ROUTER
// Detects subdomain and routes accordingly:
// - caisse.aldrins-resto.com → POS/Login interface
// - www.aldrins-resto.com → Client interface
// - aldrins-resto.com → Client interface
// - Preview URLs → Client interface by default
// ============================================

/**
 * Get the current subdomain from the hostname
 * @returns {string|null} The subdomain or null if none
 */
export const getSubdomain = () => {
    const hostname = window.location.hostname;
    
    // For preview URLs, always return null (treat as client domain)
    // Unless explicitly using caisse prefix in path
    if (hostname.includes('preview.emergentagent.com')) {
        return null;
    }
    
    // List of known main domains
    const mainDomains = [
        'aldrins-resto.com',
        'localhost'
    ];
    
    // For custom domains (e.g., caisse.aldrins-resto.com)
    for (const domain of mainDomains) {
        if (hostname.endsWith(domain)) {
            const subdomain = hostname.replace(`.${domain}`, '').replace(domain, '');
            if (subdomain && subdomain !== 'www') {
                return subdomain;
            }
            return null;
        }
    }
    
    // For other domains, extract subdomain
    const parts = hostname.split('.');
    if (parts.length > 2) {
        const subdomain = parts[0];
        if (subdomain !== 'www') {
            return subdomain;
        }
    }
    
    return null;
};

/**
 * Check if we're on the POS/Caisse subdomain
 * @returns {boolean}
 */
export const isCaisseSubdomain = () => {
    const subdomain = getSubdomain();
    return subdomain === 'caisse' || subdomain === 'pos' || subdomain === 'app';
};

/**
 * Check if we're on the client/public subdomain
 * @returns {boolean}
 */
export const isClientSubdomain = () => {
    const subdomain = getSubdomain();
    return !subdomain || subdomain === 'www' || subdomain === 'client';
};

/**
 * Get the default redirect path based on subdomain
 * @returns {string} The path to redirect to
 */
export const getDefaultPath = () => {
    if (isCaisseSubdomain()) {
        return '/login';
    }
    return '/client';
};

/**
 * Configuration object for subdomain routing
 */
export const subdomainConfig = {
    // Caisse/POS subdomain - for restaurant staff
    caisse: {
        defaultPath: '/login',
        allowedPaths: [
            '/login',
            '/dashboard',
            '/pos',
            '/clock',
            '/planning',
            '/staff',
            '/attendance',
            '/reservations',
            '/tables',
            '/kitchen',
            '/bar',
            '/products',
            '/categories',
            '/stock',
            '/reports',
            '/admin',
            '/profile',
            '/settings'
        ],
        redirectUnauthorized: '/login'
    },
    
    // Client subdomain - for restaurant customers
    client: {
        defaultPath: '/client',
        allowedPaths: [
            '/client',
            '/client/menu',
            '/client/cocktails',
            '/client/reservation',
            '/client/contact',
            '/client/fidelite',
            '/client/about',
            '/client/terms',
            '/fidelite',
            '/reservation-action'
        ],
        redirectUnauthorized: '/client'
    }
};

/**
 * Get current subdomain configuration
 * @returns {object} The configuration for the current subdomain
 */
export const getCurrentSubdomainConfig = () => {
    if (isCaisseSubdomain()) {
        return subdomainConfig.caisse;
    }
    return subdomainConfig.client;
};
